<template>
  <UiComponentWrapper class="header">
    <UiComponentWrapper
      v-if="backgroundExists"
      class="background-image"
    >
      <UiComponentImage
        v-if="!$device.isMobile"
        :key="`background-${backgroundImageX},${backgroundImageY},${backgroundImageWidth},${backgroundImageHeight}`"
        :alt="background.alt"
        :expected-size="screenWidth"
        :extra-params="{
          rect: `${backgroundImageX},${backgroundImageY},${backgroundImageWidth},${backgroundImageHeight}`,
        }"
        :height="backgroundImageHeight"
        :lazyload="false"
        :orientation="
          backgroundImageWidth > backgroundImageHeight
            ? 'landscape'
            : 'portrait'
        "
        :quality="[100, 100, 100]"
        :source="background.url"
        :width="backgroundImageWidth"
      />
    </UiComponentWrapper>
    <UiComponentContainer>
      <UiComponentHeading :level="1">
        <slot name="heading" />
      </UiComponentHeading>
      <UiComponentWrapper class="search-and-categories">
        <UiComponentWrapper class="search-mobile">
          <UiComponentParagraph
            class="icon-block"
            @click="$emit('header:clicked')"
          >
            <UiComponentIcon type="search" /><slot name="search-placeholder" />
          </UiComponentParagraph>
          <UiComponentParagraph><slot name="search-text" /></UiComponentParagraph>
        </UiComponentWrapper>
        <UiComponentWrapper
          class="search"
          @click="$emit('header:clicked')"
        >
          <UiComponentParagraph class="icon-block">
            <UiComponentIcon type="search" /><slot name="search-placeholder" />
          </UiComponentParagraph>
          <UiComponentParagraph><slot name="search-text" /></UiComponentParagraph>
        </UiComponentWrapper>
        <UiComponentWrapper>
          <UiComponentSelect
            id="select"
            :index="indexOfActiveCategory"
            :options="categoriesOptions"
            @select:change="onChange"
          />
          <UiComponentWrapper class="categories">
            <AtomButton
              v-for="({ id, name, slug }, index) in categoriesOnTag"
              :key="id"
              :class="{
                active: selectedCategory && id === selectedCategory.id,
              }"
              :to="
                index === 0
                  ? localePath('blog')
                  : localePath({
                    name: 'blog-category-slug',
                    params: {
                      slug,
                    },
                  })
              "
            >
              <UiComponentTag size="s">
                {{ name }}
              </UiComponentTag>
            </AtomButton>
          </UiComponentWrapper>
        </UiComponentWrapper>
      </UiComponentWrapper>
    </UiComponentContainer>
  </UiComponentWrapper>
</template>

<script setup>
// Composables
const localePath = useLocalePath()
const { push } = useRouter()
const { width: screenWidth } = useScreenSize()

defineEmits([
  'header:clicked',
])

// Props
const props = defineProps({
  backgroundDesktop: {
    default() {
      return {}
    },
    type: Object,
    required: false,
  },
  backgroundMobile: {
    default() {
      return {}
    },
    type: Object,
    required: false,
  },
  categories: {
    required: true,
    type: Array,
  },
  isAuthorPage: {
    default() {
      return false
    },
    required: false,
    type: Boolean,
  },
  selectedCategory: {
    default() {
      return {}
    },
    required: false,
    type: Object,
  },
  wording: {
    required: true,
    type: String,
  },
})

// Computed Properties
const background = computed(() => {
  if (!props.backgroundMobile || !props.backgroundDesktop) {
    return {}
  }

  return screenWidth.value < 768
    ? { ...props.backgroundMobile }
    : { ...props.backgroundDesktop }
})

const backgroundExists = computed(() => {
  return Object.keys(background.value).length > 0
})

const backgroundImageHeight = computed(() => {
  return screenWidth.value < 768
    ? props.isAuthorPage ? 450 * 3 : 380 * 3
    : props.isAuthorPage ? 636 * 3 : 464 * 3
})

const backgroundImageWidth = computed(() => {
  return screenWidth.value * 3
})

const backgroundImageX = computed(() => {
  return screenWidth.value < 768 ? 'right' : 'center'
})

const backgroundImageY = computed(() => {
  return screenWidth.value < 768 ? 'top' : 'middle'
})

const categoriesOnSelect = computed(() => {
  return [
    props.wording,
    ...props.categories.map(item => ({ ...item, value: item.id })),
  ]
})

const categoriesOnTag = computed(() => {
  return [
    { id: 0, name: props.wording, slug: './' },
    ...props.categories.map(item => ({ ...item, value: item.id })),
  ]
})

const categoriesOptions = computed(() => {
  return categoriesOnSelect.value.map((item, i) => {
    if (i === 0) return item
    return {
      id: item.id,
      name: item.name,
      slug: item.slug,
      value: item.id,
    }
  })
})

const indexOfActiveCategory = computed(() => {
  if (!props.selectedCategory) {
    return 0
  }

  return categoriesOnSelect.value.findIndex(
    ({ id }) => id === props.selectedCategory.id,
  )
})

// Mounted
function onChange(value) {
  const newCategory = categoriesOnSelect.value.find(
    ({ id }) => id === value,
  )
  if (newCategory && newCategory.slug) {
    push(
      localePath({
        name: 'blog-category-slug',
        params: { slug: newCategory.slug },
      }),
    )
  }
  else {
    push(
      localePath('blog'),
    )
  }
}
</script>

<style lang="postcss" scoped>
.header {
  @apply mt-6 flex-col items-center justify-center text-center;

  @screen md {
    @apply mt-24 h-auto;
  }

  @screen xl {
    @apply mt-12;
  }

  .background-image {
    @apply absolute inset-0;
    z-index: -1;

    picture {
      @apply rounded-none;
    }
  }

  .container {
    @apply flex-col;

    .search-and-categories {
      @apply mt-6 flex flex-col items-center;

      .search,
      .search-mobile {
        @apply hidden text-grey-blue-600;

        @screen md {
          @apply flex;
        }

        .icon-block {
          @apply flex mr-2 relative justify-center items-center cursor-pointer font-bold text-winter-green-900;

          i {
            @apply mr-2 text-base;
          }

          &:after {
            content: "";
            @apply absolute left-0 w-full bg-winter-green-300;
            height: 3px;
            bottom: -5px;
          }
        }

        p {
          @apply text-grey-blue-600;
        }
      }

      .search {
        @apply mb-1;
      }

      .search-mobile {
        @apply inline-flex pb-1 text-center;

        @screen md {
          @apply hidden;
        }

        .icon-block {
          @apply w-fit;
        }
      }

      .wrapper {
        @apply items-center justify-center;

        .select {
          @apply mt-4 bg-white rounded;

          @screen md {
            @apply hidden;
          }

          :deep(i) {
            z-index: 1;
          }
        }

        .categories {
          @apply hidden;

          @screen md {
            @apply flex flex-wrap justify-center;
            max-width: 606px;
          }

          .button {
            @apply mr-3 mt-4 px-0;
            height: fit-content;

            :deep(.tag) {
              @apply bg-blizzard-700 font-medium;
            }

            &.active {
              :deep(.tag) {
                @apply bg-blizzard-800;
              }
            }
          }
        }
      }
    }
  }
}

@media screen and (max-width: 342px) {
  .header {
    .container {
      .search-and-categories {
        .search-mobile {
          @apply flex flex-col pb-0 items-center;

          .icon-block + p {
            @apply mt-4;
          }
        }
      }
    }
  }
}
</style>
